import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { AxiosResponse } from 'axios'
import React, { FormEvent, useMemo } from 'react'
import { Form } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { MultiValue } from 'react-select'
import { toast } from 'react-toastify'

import { deleteRecipe, getGenerateTags, getRecipeFoodRestrictions, saveRecipe } from '@/api'
import type {
  FoodRestriction,
  LanguagesConfig,
  NewRecipe,
  NutsRegion,
  Recipe,
  RecipeSource,
  RecipeTag,
  RecipeTagCategories,
  TempRecipe,
  TempRecipeIngredient,
  User,
} from '@/api/types'
import { useAuth } from '@/auth'
import { Button } from '@/components/Button'
import { Flex } from '@/components/Flex'
import { Grid } from '@/components/Grid'
import { Label } from '@/components/Label'
import { PageEditHeader } from '@/components/PageEditHeader'
import { Radio, RadioGroup } from '@/components/RadioGroup/RadioGroup'
import { Select } from '@/components/Select'
import { Tag, TagGroup } from '@/components/TagGroup'
import { Textarea } from '@/components/Textarea'
import { TextField } from '@/components/TextField'
import { Tooltip } from '@/components/Tooltip/'
import NutritionFacts, { NutritionalValueCalculator } from '@/features/NutritionFacts'
import { useHistoryState } from '@/hooks/useHistoryState'
import { useSaveShortcut } from '@/hooks/useSaveShortcut'
import MacroKcalPie from '../../MacroKcalPie/MacroKcalPie'
import AllergensEditor from '../AllergensEditor'
import { CUISINE, EXPERTISE_LEVELS, ExpertiseLevel, STATUS, STORAGE } from '../constants'
import DietEditor from '../DietEditor'
import {
  findI18nTranslation,
  getUserFullName,
  groupTags,
  TranslationLabels,
  updateTranslationLabels,
} from '../helpers'
import IngredientsEditor from './IngredientsEditor'
import NutsSelect from './NutsSelect'
import PreparationStepsEditor from './PreparationStepsEditor'
import RecipePhotoUpload from './RecipePhotoUpload'

type Props = {
  originalRecipe: OriginalRecipe
  tags: RecipeTag[]
  allergens: FoodRestriction[]
  languages: LanguagesConfig
  lang?: string
  sources: RecipeSource[]
  authors?: User[]
}

export type OriginalRecipe = NewRecipe | TempRecipe

export default function EditRecipe({
  originalRecipe,
  tags,
  allergens,
  languages,
  lang,
  authors,
  sources,
}: Props) {
  const navigate = useNavigate()
  const formRef = useSaveShortcut()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedLanguage, setSelectedLanguage] = useHistoryState(
    'languages',
    lang || languages?.defaultLanguage,
    'PLAIN',
  )
  const [recipe, setRecipe] = useHistoryState('recipe', originalRecipe)
  const recipeTags = useMemo(() => groupTags(recipe.tags), [recipe.tags])
  const [selectedTags, setSelectedTags] = useHistoryState('tags', recipeTags)
  const groupedTags = useMemo(() => groupTags(tags), [tags])

  const auth = useAuth()
  const isDisabled =
    recipe &&
    !!recipe.idRecipe &&
    !auth.isAdmin &&
    !auth.isNutritionist &&
    recipe.author?.externalID !== auth.externalID

  const nutrition = useMemo(
    () =>
      NutritionalValueCalculator.fromRecipeIngredients(
        recipe.recipeIngredients,
        recipe.preparationDetails?.servings,
      ),
    [recipe.recipeIngredients, recipe.preparationDetails?.servings],
  )

  const ingIds = recipe.recipeIngredients.map((ing) => ing.ingredient.idIngredient)
  const { data: selectedAllergens } = useQuery({
    queryKey: ['recipeFoodRestrictions', ingIds],
    initialData: [],
    queryFn: () => getRecipeFoodRestrictions(ingIds),
    select: (tags) => tags.map((item) => item.name),
    enabled: ingIds.length > 0,
  })

  const { data: selectedDiets } = useQuery({
    queryKey: ['generateTags', ingIds],
    initialData: [],
    queryFn: () => getGenerateTags(ingIds),
    select: (tags) => tags.map((item) => item.name),
    enabled: ingIds.length > 0,
  })

  const mutation = useMutation({
    mutationFn: (formData: TempRecipe | NewRecipe) => saveRecipe(formData),
    onSuccess: (res) => {
      if (res.success) {
        if (!recipe.idRecipe) {
          queryClient.removeQueries({ queryKey: ['recipes'] })

          toast.success('Recipe created with success')

          navigate({
            to: '/recipes/$recipeId',
            params: {
              recipeId: res.data.idRecipe,
            },
          })
        } else {
          queryClient.setQueryData(['recipe', recipe.idRecipe], res.data)
          updateRecipesQueries(queryClient, res.data)
          toast.success('Saved with success')
        }
      } else {
        toast.error(res.errorMessage)
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteRecipe(recipe.idRecipe ?? 0),
    onSuccess: () => {
      queryClient.setQueriesData(
        { queryKey: ['recipes'] },
        (oldData: AxiosResponse<Recipe[], any>) => {
          return {
            ...oldData,
            data: oldData.data.filter((item: Recipe) => item.idRecipe !== recipe.idRecipe),
          }
        },
      )

      toast.success('Deleted with success')

      navigate({
        to: '/recipes',
        state: (old) => {
          return {
            ...old,
            deleteId: recipe.idRecipe,
          }
        },
      })
    },
    onError: () => {
      toast.error('Error deleting recipe')
    },
  })

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault()
    const updatedTags = Object.values(selectedTags)
      .filter((item) => item !== undefined)
      .flat(2)

    // The user filled the fields for a secondary language,
    // we allow it to save it with a generated name and will automatically translate to the default language
    if (
      !recipe.name &&
      selectedLanguage !== languages.defaultLanguage &&
      findI18nTranslation(recipe, selectedLanguage, 'PREPARATION_STEPS')
    ) {
      recipe.name = 'Untitled Recipe'
    }

    mutation.mutate({ ...recipe, tags: updatedTags })
  }

  const handleDeleteRecipe = () => {
    if (confirm('Are sure you want to delete the recipe?')) {
      deleteMutation.mutate()
    }
  }

  const handleChangeInput = <K extends keyof TempRecipe>(key: K, value: TempRecipe[K]) => {
    setRecipe({
      ...recipe,
      [key]: value,
    })
  }

  const handleRadioGroupChange = (category: RecipeTagCategories, value: string) => {
    const tag = tags.find((exp) => exp.name === value)

    setSelectedTags({
      ...selectedTags,
      [category]: tag ? [tag] : undefined,
    })
  }

  const handleSelectChange = (setName: RecipeTagCategories, selection: MultiValue<RecipeTag>) => {
    setSelectedTags({
      ...selectedTags,
      [setName]: selection,
    })
  }

  const handlePreparationDetailsChange = (key: string, value: unknown) => {
    setRecipe({
      ...recipe,
      preparationDetails: {
        ...recipe.preparationDetails,
        [key]: value,
      },
    })
  }

  const updateIngredients = (recipeIngredients: TempRecipeIngredient[]) => {
    setRecipe({
      ...recipe,
      recipeIngredients,
    })
  }

  function updateTagsSelection(category: RecipeTagCategories, selection: Set<string>) {
    setSelectedTags({
      ...selectedTags,
      [category]: tags.filter((tag) => Array.from(selection).includes(tag.name)),
    })
  }

  const getTranslation = (label: TranslationLabels) => {
    if (!selectedLanguage || selectedLanguage === languages.defaultLanguage) {
      switch (label) {
        case 'NAME':
          return recipe.name
        case 'PREPARATION_STEPS':
          return recipe.preparationDetails?.preparationSteps
        case 'OTHER_INFO':
          return recipe.otherInfo
      }
    }

    return findI18nTranslation(recipe, selectedLanguage, label)
  }

  const updateTranslations = (label: TranslationLabels, translation: string) => {
    if (!selectedLanguage || selectedLanguage === languages.defaultLanguage) {
      switch (label) {
        case 'NAME':
          handleChangeInput('name', translation)
          break
        case 'PREPARATION_STEPS':
          handlePreparationDetailsChange('preparationSteps', translation)
          break
        case 'OTHER_INFO':
          handleChangeInput('otherInfo', translation)
          break
      }
      return
    }

    //find if the translation already exists
    const existingTranslations = updateTranslationLabels(
      recipe,
      selectedLanguage,
      label,
      translation,
    )

    setRecipe({
      ...recipe,
      i18nLabels: [...existingTranslations],
    })
  }

  function updateAuthor(authorId: string) {
    const author = authors?.find((author) => author.externalID === authorId)
    setRecipe({
      ...recipe,
      author,
    })
  }

  const statusOptions = useMemo(
    () =>
      STATUS.map((status: (typeof STATUS)[number]) => ({
        value: status,
        label: t(`recipe.status.${status}`),
      })),
    [],
  )

  const author = recipe.author ?? authors?.find((a) => a.externalID === auth.externalID)
  const authorLabel = getUserFullName(author)

  return (
    <Form onSubmit={handleSubmit} ref={formRef}>
      <PageEditHeader
        title={t('recipe.editRecipe')}
        renderTitleActions={() => (
          <>
            {auth.isAdmin && (
              <Select
                value={{
                  value: author?.externalID,
                  label: `${t('recipe.author')}: ${authorLabel}`,
                }}
                options={
                  authors?.map((author) => ({
                    value: author.externalID,
                    label: getUserFullName(author),
                  })) ?? []
                }
                onChange={(selection) => (selection ? updateAuthor(selection?.value || '') : null)}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    width: '250px',
                  }),
                }}></Select>
            )}
            {languages && (
              <Select
                isDisabled={isDisabled}
                value={{
                  value: selectedLanguage,
                  label: languages.languages.find((l) => l.code === selectedLanguage)?.name,
                }}
                options={languages.languages.map((lang) => ({
                  value: lang.code,
                  label: lang.name,
                }))}
                onChange={(selection) => setSelectedLanguage(selection?.value)}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    width: '200px',
                  }),
                }}
              />
            )}
            <Select
              isDisabled={isDisabled}
              value={{ value: recipe.status, label: t(`recipe.status.${recipe.status}`) }}
              options={statusOptions}
              onChange={(selection) =>
                handleChangeInput('status', selection ? selection.value : 'DRAFT')
              }
            />
            {recipe.idRecipe && (
              <Button asChild>
                <Link
                  to="/recipes/duplicate/$recipeId"
                  params={{ recipeId: recipe.idRecipe?.toString() }}>
                  {t('recipe.duplicate')}
                </Link>
              </Button>
            )}
          </>
        )}>
        {recipe.idRecipe && !isDisabled && (
          <Button variant="negative" onPress={handleDeleteRecipe}>
            {t('recipe.deleteRecipe')}
          </Button>
        )}
        {!isDisabled && (
          <Button variant="primary" type="submit">
            {t('recipe.saveRecipe')}
          </Button>
        )}
      </PageEditHeader>

      <Grid columns="1fr 320px">
        <div className="panel">
          <Grid columns="4fr 1fr">
            <TextField
              isDisabled={isDisabled}
              isRequired
              label={t('recipe.recipe')}
              value={getTranslation('NAME')}
              errorMessage={t('recipe.errorNameEmpty')}
              onChange={(value) => updateTranslations('NAME', value)}
            />

            <TextField
              label={t('recipe.servings')}
              isDisabled={isDisabled}
              isRequired
              type="number"
              value={recipe.preparationDetails?.servings?.toString() ?? '1'}
              errorMessage={t('recipe.errorPortionsEmpty')}
              onChange={(value) => handlePreparationDetailsChange('servings', value)}
            />
          </Grid>
          <TagGroup
            disabledKeys={isDisabled ? groupedTags.MEAL_TYPE.map((tag) => tag.name) : []}
            label={t('recipe.mealType')}
            selectionMode="multiple"
            items={groupedTags.MEAL_TYPE}
            disallowEmptySelection
            selectedKeys={selectedTags.MEAL_TYPE?.map((item) => item.name)}
            onSelectionChange={(selection: Set<string>) =>
              updateTagsSelection('MEAL_TYPE', selection)
            }>
            {(item) => <Tag id={item.name}>{t(`recipe.mealTypeOptions.${item.name}` as any)}</Tag>}
          </TagGroup>

          <Grid columns="1fr 1fr">
            <div>
              <Label>{t('recipe.cuisine')}</Label>
              <Select
                isDisabled={isDisabled}
                isClearable
                getOptionLabel={(option) =>
                  option?.name ? t(`recipe.cuisines.${option.name}` as any) : ''
                }
                getOptionValue={(option) => option.name as string}
                onChange={(selection) => handleChangeInput('cuisine', selection?.name)}
                value={{ name: recipe.cuisine }}
                options={CUISINE.map((cuisine: (typeof CUISINE)[number]) => ({
                  name: cuisine,
                }))}
              />
            </div>

            <div>
              <Label>NUTS</Label>
              <NutsSelect
                isDisabled={isDisabled}
                value={recipe.nutsRegion}
                onChange={(value: NutsRegion) => {
                  setRecipe({
                    ...recipe,
                    nutsRegion: value,
                  })
                }}
              />
            </div>
          </Grid>

          <div>
            <Label>{t('recipe.expertise')}</Label>
            <RadioGroup
              isDisabled={isDisabled}
              variation="segmented"
              value={recipe.expertiseLevel}
              onChange={(value: ExpertiseLevel) => handleChangeInput('expertiseLevel', value)}>
              {EXPERTISE_LEVELS.map((tag: ExpertiseLevel) => (
                <Radio key={tag} value={tag}>
                  {t(`recipe.expertiseOptions.${tag}`)}
                </Radio>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label>{t('recipe.storage')}</Label>
            <RadioGroup
              isDisabled={isDisabled}
              variation="segmented"
              value={getSingleTag(selectedTags.STORAGE)}
              onChange={(value) => handleRadioGroupChange('STORAGE', value)}>
              {STORAGE.map((tag: (typeof STORAGE)[number]) => (
                <Radio key={tag} value={tag}>
                  {t(`recipe.storageOptions.${tag}`)}
                </Radio>
              ))}
            </RadioGroup>
          </div>

          <TagGroup
            disabledKeys={isDisabled ? groupedTags.SEASON.map((tag) => tag.name) : []}
            label={t('recipe.season')}
            selectionMode="multiple"
            items={groupedTags.SEASON}
            selectedKeys={selectedTags.SEASON?.map((item) => item.name)}
            onSelectionChange={(selection: Set<string>) =>
              updateTagsSelection('SEASON', selection)
            }>
            {(item) => <Tag id={item.name}>{t(`recipe.seasonOptions.${item.name}` as any)}</Tag>}
          </TagGroup>

          <div>
            <Label>{t('recipe.cookingMethod')}</Label>
            <Select
              isMulti
              isDisabled={isDisabled}
              closeMenuOnSelect={false}
              getOptionLabel={(option) => t(`recipe.cookingMethods.${option.name}` as any)}
              getOptionValue={(option) => option.name}
              onChange={(selection) => handleSelectChange('COOKING_METHOD', selection)}
              value={selectedTags.COOKING_METHOD ?? []}
              options={groupedTags.COOKING_METHOD ?? []}
            />
          </div>

          <div>
            <Label>{t('recipe.otherAttributes')}</Label>
            <Select
              isDisabled={isDisabled}
              isMulti
              closeMenuOnSelect={false}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.name}
              onChange={(selection) => handleSelectChange('OTHER', selection)}
              value={selectedTags.OTHER ?? []}
              options={groupedTags.OTHER ?? []}
            />
          </div>

          <IngredientsEditor
            isDisabled={isDisabled}
            recipeIngredients={recipe.recipeIngredients}
            language={selectedLanguage}
            updateIngredients={updateIngredients}
          />

          <PreparationStepsEditor
            isDisabled={isDisabled}
            label={t('recipe.preparation')}
            content={getTranslation('PREPARATION_STEPS') || ''}
            onUpdate={(value) => updateTranslations('PREPARATION_STEPS', value)}
          />

          <Flex>
            <TextField
              isDisabled={isDisabled}
              label={t('recipe.preparationTime')}
              type="number"
              value={recipe.preparationDetails?.preparationTime?.toString()}
              errorMessage={t('recipe.errorNameEmpty')}
              onChange={(value) => handlePreparationDetailsChange('preparationTime', value)}
            />
            <TextField
              label={t('recipe.cookingTime')}
              isDisabled={isDisabled}
              type="number"
              value={recipe.preparationDetails?.cookingTime?.toString()}
              errorMessage={t('recipe.errorNameEmpty')}
              onChange={(value) => handlePreparationDetailsChange('cookingTime', value)}
            />
          </Flex>

          <Textarea
            label={t('recipe.otherInfo')}
            isDisabled={isDisabled}
            value={getTranslation('OTHER_INFO')}
            onChange={(value) => updateTranslations('OTHER_INFO', value)}
          />

          <div>
            <Label>{t('recipe.source')}</Label>
            <Select
              isDisabled={isDisabled}
              isClearable
              getOptionLabel={(option) => option?.name ?? ''}
              onChange={(selection) => handleChangeInput('source', selection || undefined)}
              value={recipe.source}
              options={sources}
            />
          </div>
        </div>

        <Flex direction="column">
          <RecipePhotoUpload recipe={recipe} isDisabled={isDisabled} />

          <div className="panel-xs">
            <DietEditor
              diets={groupedTags.DIET}
              recipeDiets={selectedTags.DIET}
              selectedDiets={selectedDiets}
              isDisabled={true}
            />
            <AllergensEditor
              allergens={allergens}
              selectedAllergens={selectedAllergens}
              isDisabled={true}
            />
          </div>
          <NutritionFacts nutritionalValue={nutrition} />
          <div className="panel-xs">
            <Label>{t('recipe.macroDistributionSmallTitle')}</Label>
            <MacroKcalPie
              data={
                nutrition
                  ? {
                      proteinKcal: nutrition.protein * 4,
                      fatKcal: nutrition.fat * 9,
                      carbohydratesKcal: nutrition.carbohydrate * 4,
                    }
                  : { proteinKcal: 0, fatKcal: 0, carbohydratesKcal: 0 }
              }
              options={{
                fontSize: '1.4rem',
                labelRadius: 190,
                height: 600,
                width: 600,
              }}
            />
          </div>
        </Flex>
      </Grid>
      {recipe.createdBy && recipe.updatedAt && (
        <div
          className="container"
          style={{ color: 'gray', fontSize: 'small', paddingTop: '5px', paddingLeft: '15px' }}>
          Created by <span style={{ fontWeight: 'bold' }}>{getUserFullName(recipe.createdBy)}</span>
          . Last edited by{' '}
          <span style={{ fontWeight: 'bold' }}>{getUserFullName(recipe.updatedBy)}</span> on{' '}
          {new Date(recipe.updatedAt).toLocaleString()}
        </div>
      )}
    </Form>
  )
}

function updateRecipesQueries(queryClient: QueryClient, recipe: Recipe) {
  const queries = queryClient.getQueriesData<{ data: Recipe[] }>({
    queryKey: ['recipes'],
  })

  for (const query of queries) {
    const data = query[1]

    if (data) {
      data.data = data.data.map((item) => (item.idRecipe === recipe.idRecipe ? recipe : item))
      queryClient.setQueryData(query[0], { ...data })
    }
  }
}

function getSingleTag(tags: RecipeTag[]) {
  return tags && tags[0].name
}
